import { Box, FormHelperText, FormLabel, Stack } from '@mui/material';
import { useController, useFormContext } from 'react-hook-form';
import { ErrorOutlineRounded } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateRangeSchema } from '../dateRangeSchema';
import { useCallback, useState } from 'react';
import { useMarkupTFunction } from '@retail/markup/i18n';
import { useMountEffect } from '@shared/hooks';

interface Props {
  excludeToDate?: boolean;
  alignment?: 'row' | 'column';
  labelAlignment?: 'row' | 'column';
  inputFieldWidth?: number;
  defaultFromDate?: Date;
  defaultToDate?: Date;
}

export function ConditionDatesForm({
  excludeToDate = false,
  alignment = 'row',
  labelAlignment = 'column',
  inputFieldWidth,
  defaultFromDate,
  defaultToDate,
}: Props) {
  const t = useMarkupTFunction();

  const { control } = useFormContext<DateRangeSchema>();
  const fromDateController = useController({ name: 'fromDate', control });
  const toDateController = useController({ name: 'toDate', control });

  const [fromDate, setFromDate] = useState<Date>(
    (defaultFromDate && defaultFromDate < new Date() ? undefined : defaultFromDate) ??
    fromDateController.field.value
  );

  useMountEffect(() => {
    if (fromDate !== fromDateController.field.value) fromDateController.field.onChange(fromDate);
  });

  const handleSetFromDate = useCallback(
    (date: Date) => {
      fromDateController.field.onChange(date);
      setFromDate(date);
    },
    [fromDateController.field]
  );

  return (
    <Stack flexDirection={alignment} gap={2}>
      <Box
        display="flex"
        flexDirection={labelAlignment}
        gap={labelAlignment === 'row' ? 0 : 0.5}
        alignItems={labelAlignment === 'row' ? 'center' : 'flex-start'}
        width="100%"
      >
        <FormLabel
          sx={{ fontWeight: 500, width: 150 }}
          error={!!fromDateController.fieldState.error}
          required
        >
          {t('markup.fromDate')}
        </FormLabel>

        <Stack gap={1} overflow="hidden">
          <DatePicker
            {...fromDateController.field}
            value={fromDate}
            onChange={(value) => value && handleSetFromDate(value)}
            minDate={new Date()}
            maxDate={toDateController.field.value ?? undefined}
            format="dd/MM/yyyy"
            slotProps={{
              textField: {
                InputProps: {
                  sx: {
                    borderRadius: 2, height: 40, '& fieldset': {
                      borderColor: 'divider',
                    },
                  }
                },
                sx: { width: inputFieldWidth ?? 'fit-content' },
                helperText: undefined,
              },
            }}
          />

          {!!fromDateController.fieldState.error && (
            <Stack direction="row" spacing={1} flexWrap="nowrap" alignItems="center">
              <ErrorOutlineRounded color="warning" fontSize="small" />
              <FormHelperText sx={{ color: 'warning.main' }}>
                {fromDateController.fieldState.error.message}
              </FormHelperText>
            </Stack>
          )}
        </Stack>
      </Box>
      {!excludeToDate && (
        <Box
          display="flex"
          flexDirection={labelAlignment}
          gap={labelAlignment === 'row' ? 0 : 0.5}
          alignItems={labelAlignment === 'row' ? 'center' : 'flex-start'}
          width="100%"
        >
          <FormLabel sx={{ fontWeight: 500, width: 150 }}>{t('markup.toDate')}</FormLabel>
          <DatePicker
            {...toDateController.field}
            value={toDateController.field.value ?? defaultToDate ?? null}
            minDate={fromDate > new Date() ? fromDate : new Date()}
            format="dd/MM/yyyy"
            slotProps={{
              field: { clearable: true },
              textField: {
                InputProps: {
                  sx: {
                    borderRadius: '16px !important', height: 40, '& fieldset': {
                      borderColor: 'divider',
                    },
                  }
                },
                sx: { width: inputFieldWidth ?? 'fit-content' },
                helperText: undefined,
              },
            }}
          />
        </Box>
      )}
    </Stack>
  );
}

